import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import StatusBadge from '../components/StatusBadge';
import { localeLabel } from '../utils/localeFlag';

const ALL_TYPES = ['PRODUCT', 'COLLECTION', 'PAGE', 'ARTICLE', 'MENU'];

export default function BulkTranslate() {
  const [locales, setLocales] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(new Set(ALL_TYPES));
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [parentJobId, setParentJobId] = useState(null);
  const [parentJob, setParentJob] = useState(null);

  useEffect(() => {
    axios.get('api/transly/locales/').then((res) => {
      const nonPrimary = res.data.filter((l) => !l.primary);
      setLocales(nonPrimary);
      if (nonPrimary.length > 0) setSelectedLocale(nonPrimary[0].locale);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!parentJobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('api/transly/jobs/', { params: { id: parentJobId } });
        setParentJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') clearInterval(interval);
      } catch { clearInterval(interval); }
    }, 2000);
    return () => clearInterval(interval);
  }, [parentJobId]);

  const toggleType = (type) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await axios.post('api/transly/bulk/', { targetLocale: selectedLocale, resourceTypes: Array.from(selectedTypes) });
      setParentJobId(res.data.jobId);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setStarting(false);
    }
  };

  const isRunning = parentJob && parentJob.status === 'in_progress';
  const isDone = parentJob && (parentJob.status === 'completed' || parentJob.status === 'failed');

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">Bulk Translate</h1>

      {!parentJobId ? (
        <>
          <Card className="mb-6">
            <CardHeader><CardTitle>Target Locale</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2">
                {locales.map((l) => (
                  <Button key={l.locale} variant={selectedLocale === l.locale ? 'default' : 'outline'} size="sm" onClick={() => setSelectedLocale(l.locale)}>
                    {localeLabel(l.locale)} - {l.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader><CardTitle>Resource Types</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {ALL_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTypes.has(type)}
                      onChange={() => toggleType(type)}
                      className="rounded border-input"
                    />
                    <Label className="cursor-pointer">{type}</Label>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {error && <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive mb-4">{error}</div>}

          <Button className="w-full" size="lg" onClick={handleStart} disabled={starting || selectedTypes.size === 0}>
            {starting ? 'Starting...' : 'Start Bulk Translation'}
          </Button>
        </>
      ) : (
        <>
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{isRunning ? 'Translation in progress...' : parentJob?.status === 'completed' ? 'Completed' : 'Failed'}</CardTitle>
                <StatusBadge status={parentJob?.status} />
              </div>
            </CardHeader>
            <CardContent>
              {parentJob && parentJob.total_resources > 0 && (
                <div className="mb-5">
                  <div className="flex justify-between text-sm text-muted-foreground mb-1">
                    <span>Overall</span>
                    <span>{parentJob.completed_count + parentJob.failed_count} / {parentJob.total_resources}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${parentJob.failed_count > 0 ? 'bg-yellow-500' : 'bg-primary'}`}
                      style={{ width: `${Math.min(100, ((parentJob.completed_count + parentJob.failed_count) / parentJob.total_resources) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {(parentJob?.waves || []).map((wave) => (
                <div key={wave.id} className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-foreground">{wave.resource_type}</span>
                    <span className="text-muted-foreground">
                      {wave.completed_count}/{wave.total_resources}
                      {wave.failed_count > 0 && <span className="text-destructive ml-1">({wave.failed_count} failed)</span>}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        wave.status === 'in_progress' ? 'bg-primary' : wave.failed_count > 0 ? 'bg-yellow-400' : 'bg-emerald-500'
                      }`}
                      style={{ width: wave.total_resources > 0 ? `${((wave.completed_count + wave.failed_count) / wave.total_resources) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {isDone && (
            <Button variant="secondary" className="w-full" size="lg" onClick={() => { setParentJobId(null); setParentJob(null); setError(null); }}>
              Start New Bulk Translation
            </Button>
          )}
        </>
      )}
    </div>
  );
}
